import { CreateUserDto, UserFormShape } from "@containers/User/interfaces";
import { FORM_ERROR_MESSAGES } from "@shared/constants";
import { Hospital, User } from "@shared/models";
import { actions as hospitalActions, selectors as hospitalSelectors } from "@containers/Hospital/store/";
import * as Yup from "yup";
import { Option } from "@shared/interfaces";
import { PHONE_NUMBER_REGEXP } from "@shared/constants/regexpMatches";

const commonValidationSchema = {
  first_name: Yup.string().max(35, `First Name ${FORM_ERROR_MESSAGES.LONG}`).required(FORM_ERROR_MESSAGES.REQUIRED),
  last_name: Yup.string().max(35, `Last Name ${FORM_ERROR_MESSAGES.LONG}`).required(FORM_ERROR_MESSAGES.REQUIRED),
  login: Yup.string(),
  phone: Yup.string()
    .min(10, `Phone ${FORM_ERROR_MESSAGES.SHORT}`)
    .matches(PHONE_NUMBER_REGEXP, FORM_ERROR_MESSAGES.PHONE_NUMBER)
    .required(FORM_ERROR_MESSAGES.REQUIRED),
  role_id: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED).nullable(),
  hospitals: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
        label: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
      }),
    )
    .min(1, "Need to select at least 1 hospital"),
};

export const createValidationSchemaSchema: Yup.SchemaOf<UserFormShape> = Yup.object().shape({
  ...commonValidationSchema,
  login: Yup.string()
    .email(FORM_ERROR_MESSAGES.EMAIL)
    .max(150, `Email ${FORM_ERROR_MESSAGES.LONG}`)
    .required(FORM_ERROR_MESSAGES.REQUIRED),
});

export const editValidationSchemaSchema: Yup.SchemaOf<UserFormShape> = Yup.object().shape({
  ...commonValidationSchema,
});

export const getValidationSchema = (user?: User | null) => {
  return user ? editValidationSchemaSchema : createValidationSchemaSchema;
};

export const handlers = {
  hospitals: {
    getData: (search: string) =>
      hospitalActions.getHospitals.request({
        search,
        page: 0,
        limit: 20,
      }),
    selectData: hospitalSelectors.getHospitals,
    prepareOptionFunction: (item: Hospital): Option => {
      return { label: item.name, value: String(item.id) };
    },
  },
};

export const getInitValues = (user: User | null, hospitalId?: number): UserFormShape => {
  const defaultHospital = hospitalId
    ? [
        {
          value: String(hospitalId),
          label: "Default hospital",
        },
      ]
    : [];

  return {
    login: user?.login || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
    role_id: user?.user_roles?.length ? String(user.user_roles[0].role_id) : null,
    hospitals:
      user?.user_roles?.map((ur) => ({
        value: ur.hospital ? String(ur.hospital?.id) : "-1",
        label: ur.hospital ? ur.hospital?.name : "All hospitals",
      })) || defaultHospital,
  };
};

export const prepareSubmitValue = (value: UserFormShape): CreateUserDto => ({
  first_name: value.first_name,
  last_name: value.last_name,
  role_id: value.role_id,
  login: value.login,
  phone: value.phone,
  hospital_ids: value.hospitals.map((s) => (Number(s.value) > 0 ? Number(s.value) : null)),
});
